import { forwardRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  iconLeft?: ReactNode
  /** Rendered absolutely on the right (e.g. a show/hide password toggle). */
  rightSlot?: ReactNode
}

export const fieldLabelStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--ink-1)',
  marginBottom: 6,
  display: 'block',
} as const

export const fieldBaseStyle = {
  width: '100%',
  fontFamily: 'var(--font-body)',
  fontSize: 16,
  color: 'var(--ink-0)',
  padding: '11px 14px',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--border-strong)',
  background: 'var(--surface-card)',
  outline: 'none',
  boxShadow: 'var(--shadow-xs)',
} as const

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, iconLeft, rightSlot, style, id, ...rest },
  ref,
) {
  const input = (
    <div style={{ position: 'relative' }}>
      {iconLeft && (
        <span
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--ink-3)',
            display: 'inline-flex',
            pointerEvents: 'none',
          }}
        >
          {iconLeft}
        </span>
      )}
      <input
        ref={ref}
        id={id}
        style={{
          ...fieldBaseStyle,
          paddingLeft: iconLeft ? 40 : 14,
          paddingRight: rightSlot ? 40 : 14,
          ...style,
        }}
        {...rest}
      />
      {rightSlot}
    </div>
  )

  if (!label) return input
  return (
    <label style={{ display: 'block' }}>
      <span style={fieldLabelStyle}>{label}</span>
      {input}
    </label>
  )
})

export default Input
